import { format } from 'date-fns'
import { de, enUS, ko, type Locale, zhCN } from 'date-fns/locale'

import { AppLocale, defaultLocale, normalizeAppLocale } from '@/i18n/config'

type DatePatternKey =
  | 'expiryTab'
  | 'expiryTabWithYear'
  | 'expiryFull'
  | 'shortDateWithYear'
  | 'monthTick'

const dateFnsLocales: Record<AppLocale, Locale> = {
  en: enUS,
  'zh-CN': zhCN,
  de,
  ko,
}

const datePatterns: Record<DatePatternKey, Record<AppLocale, string>> = {
  expiryTab: {
    en: 'dd MMM',
    'zh-CN': 'M月d日',
    de: 'dd. MMM',
    ko: 'M월 d일',
  },
  expiryTabWithYear: {
    en: 'dd MMM yy',
    'zh-CN': 'yy年M月d日',
    de: 'dd. MMM yy',
    ko: 'yy년 M월 d일',
  },
  expiryFull: {
    en: 'MMM d, yyyy',
    'zh-CN': 'yyyy年M月d日',
    de: 'd. MMMM yyyy',
    ko: 'yyyy년 M월 d일',
  },
  shortDateWithYear: {
    en: 'dd-MMM-yy',
    'zh-CN': 'yy/MM/dd',
    de: 'dd.MM.yy',
    ko: 'yy.MM.dd',
  },
  // A month on its own, for a chart's month-boundary tick ("Mar").
  monthTick: {
    en: 'MMM',
    'zh-CN': 'M月',
    de: 'MMM',
    ko: 'M월',
  },
}

export function normalizeDateLocale(locale: string | undefined): AppLocale {
  return (locale && normalizeAppLocale(locale)) || defaultLocale
}

export function getDateFnsLocale(locale: string | undefined): Locale {
  return dateFnsLocales[normalizeDateLocale(locale)]
}

export function formatLocalizedDate(
  date: Date,
  locale: string | undefined,
  patternKey: DatePatternKey
): string {
  const normalizedLocale = normalizeDateLocale(locale)

  return format(date, datePatterns[patternKey][normalizedLocale], {
    locale: dateFnsLocales[normalizedLocale],
  })
}

export function toLocalDateFromUtcDate(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}
